import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Search, Calendar, Building2, Filter, 
  ChevronDown, ChartPie, DollarSign, Clock, 
  Target, Users, Settings, AlertCircle, Zap
} from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';

// Define comprehensive interfaces for detailed tracking
interface PromotionCampaign {
  id: string;
  title: string;
  description: string;
  type: 'featured' | 'spotlight' | 'premium';
  status: 'draft' | 'active' | 'paused' | 'completed' | 'archived';
  
  // Marketing Information
  marketingDetails: {
    targetAudience: string[];
    marketingChannels: string[];
    keyMessagePoints: string[];
    uniqueSellingProposition: string;
  };

  // Administrative Tracking
  administrativeInfo: {
    owner: {
      id: string;
      name: string;
      email: string;
      department: string;
    };
    organization: {
      id: string;
      name: string;
      type: string;
    };
    approvalStatus: 'pending' | 'approved' | 'rejected';
    approvedBy?: {
      name: string;
      date: string;
    };
  };

  // Technical Specifications
  technicalSpecs: {
    platform: string[];
    integrations: string[];
    trackingPixels: string[];
    conversionGoals: {
      type: string;
      target: number;
      current: number;
    }[];
    aiOptimizationEnabled: boolean;
  };

  // Performance Metrics
  metrics: {
    impressions: number;
    clicks: number;
    ctr: number;
    costPerClick: number;
    totalSpend: number;
    roi: number;
  };

  // Scheduling and Budget
  scheduling: {
    startDate: string;
    endDate: string;
    timezone: string;
  };
  budget: {
    total: number;
    spent: number;
    dailyLimit: number;
    currency: string;
  };
}

const PromotionAdminPage: React.FC = () => {
    useBreadcrumbs([
      { label: 'Promotions Management', href: '#' },
      { label: 'Marketing Information' }
    ]);
  const [campaigns, setCampaigns] = useState<PromotionCampaign[]>([
    {
      id: 'campaign001',
      title: 'Summer Resort Getaway',
      description: 'Exclusive summer promotion for luxury resort bookings',
      type: 'premium',
      status: 'active',
      
      marketingDetails: {
        targetAudience: ['Luxury Travelers', 'Couples', 'Families'],
        marketingChannels: ['Social Media', 'Email', 'Display Ads'],
        keyMessagePoints: [
          'Unparalleled Luxury',
          'Exclusive Summer Rates',
          'Limited Time Offer'
        ],
        uniqueSellingProposition: 'Most comprehensive summer resort package'
      },

      administrativeInfo: {
        owner: {
          id: 'user123',
          name: 'Sarah Johnson',
          email: 'sarah.johnson@company.com',
          department: 'Marketing'
        },
        organization: {
          id: 'org456',
          name: 'Luxury Resorts International',
          type: 'Hospitality'
        },
        approvalStatus: 'approved',
        approvedBy: {
          name: 'Michael Chang',
          date: '2024-02-10'
        }
      },

      technicalSpecs: {
        platform: ['Google Ads', 'Facebook Ads', 'Instagram'],
        integrations: ['Google Analytics', 'Salesforce', 'HubSpot'],
        trackingPixels: [
          'Google Conversion Tracking',
          'Facebook Pixel'
        ],
        conversionGoals: [
          {
            type: 'Booking Completion',
            target: 500,
            current: 245
          },
          {
            type: 'Email Signup',
            target: 1000,
            current: 672
          }
        ],
        aiOptimizationEnabled: true
      },

      metrics: {
        impressions: 125000,
        clicks: 6250,
        ctr: 5.0,
        costPerClick: 2.50,
        totalSpend: 15625,
        roi: 3.5
      },

      scheduling: {
        startDate: '2024-06-01',
        endDate: '2024-08-31',
        timezone: 'UTC-5'
      },

      budget: {
        total: 25000,
        spent: 15625,
        dailyLimit: 500,
        currency: 'USD'
      }
    }
  ]);

  const [activeTab, setActiveTab] = useState<'overview' | 'marketing' | 'admin' | 'technical'>('overview');

  const renderOverviewTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Performance Summary */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center mb-4">
          <ChartPie className="h-6 w-6 text-blue-600 mr-3" />
          <h3 className="text-lg font-semibold text-gray-900">Performance Overview</h3>
        </div>
        <div className="space-y-3">
          <div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>Total Impressions</span>
              <span>{campaigns[0].metrics.impressions.toLocaleString()}</span>
            </div>
            <div className="w-full bg-gray-200 h-2 rounded-full mt-1">
              <div 
                className="bg-blue-500 h-2 rounded-full" 
                style={{width: `${(campaigns[0].metrics.impressions / 150000) * 100}%`}}
              />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>Click-Through Rate</span>
              <span>{campaigns[0].metrics.ctr}%</span>
            </div>
            <div className="w-full bg-gray-200 h-2 rounded-full mt-1">
              <div 
                className="bg-green-500 h-2 rounded-full" 
                style={{width: `${campaigns[0].metrics.ctr * 2}%`}}
              />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>Return on Investment</span>
              <span>{campaigns[0].metrics.roi}x</span>
            </div>
            <div className="w-full bg-gray-200 h-2 rounded-full mt-1">
              <div 
                className="bg-purple-500 h-2 rounded-full" 
                style={{width: `${campaigns[0].metrics.roi * 20}%`}}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Budget Tracking */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center mb-4">
          <DollarSign className="h-6 w-6 text-green-600 mr-3" />
          <h3 className="text-lg font-semibold text-gray-900">Budget Management</h3>
        </div>
        <div className="space-y-3">
          <div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>Total Budget</span>
              <span>${campaigns[0].budget.total.toLocaleString()}</span>
            </div>
          </div>
          <div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>Spent</span>
              <span>${campaigns[0].budget.spent.toLocaleString()}</span>
            </div>
            <div className="w-full bg-gray-200 h-2 rounded-full mt-1">
              <div 
                className="bg-blue-500 h-2 rounded-full" 
                style={{width: `${(campaigns[0].budget.spent / campaigns[0].budget.total) * 100}%`}}
              />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>Daily Limit</span>
              <span>${campaigns[0].budget.dailyLimit.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Campaign Timeline */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center mb-4">
          <Clock className="h-6 w-6 text-yellow-600 mr-3" />
          <h3 className="text-lg font-semibold text-gray-900">Campaign Timeline</h3>
        </div>
        <div className="space-y-3">
          <div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>Start Date</span>
              <span>{new Date(campaigns[0].scheduling.startDate).toLocaleDateString()}</span>
            </div>
          </div>
          <div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>End Date</span>
              <span>{new Date(campaigns[0].scheduling.endDate).toLocaleDateString()}</span>
            </div>
          </div>
          <div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>Duration</span>
              <span>
                {Math.ceil((new Date(campaigns[0].scheduling.endDate).getTime() - new Date(campaigns[0].scheduling.startDate).getTime()) / (1000 * 3600 * 24))} days
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMarketingTab = () => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center mb-6">
        <Target className="h-6 w-6 text-blue-600 mr-3" />
        <h3 className="text-lg font-semibold text-gray-900">Marketing Details</h3>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-md font-semibold text-gray-800 mb-3">Target Audience</h4>
          <ul className="space-y-2">
            {campaigns[0].marketingDetails.targetAudience.map((audience, index) => (
              <li key={index} className="flex items-center">
                <Zap className="h-4 w-4 text-blue-500 mr-2" />
                <span className="text-sm text-gray-700">{audience}</span>
              </li>
            ))}
          </ul>
        </div>
        
        <div>
          <h4 className="text-md font-semibold text-gray-800 mb-3">Marketing Channels</h4>
          <ul className="space-y-2">
            {campaigns[0].marketingDetails.marketingChannels.map((channel, index) => (
              <li key={index} className="flex items-center">
                <Users className="h-4 w-4 text-green-500 mr-2" />
                <span className="text-sm text-gray-700">{channel}</span>
              </li>
            ))}
          </ul>
        </div>
        
        <div className="md:col-span-2">
          <h4 className="text-md font-semibold text-gray-800 mb-3">Key Message Points</h4>
          <div className="grid md:grid-cols-3 gap-3">
            {campaigns[0].marketingDetails.keyMessagePoints.map((point, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center mb-2">
                  <AlertCircle className="h-5 w-5 text-yellow-500 mr-2" />
                  <span className="text-sm font-medium text-gray-800">Message {index + 1}</span>
                </div>
                <p className="text-sm text-gray-600">{point}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderAdminTab = () => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center mb-6">
        <Settings className="h-6 w-6 text-purple-600 mr-3" />
        <h3 className="text-lg font-semibold text-gray-900">Administrative Information</h3>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-md font-semibold text-gray-800 mb-3">Campaign Owner</h4>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center mb-2">
              <div className="h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-purple-600 font-bold">
                  {campaigns[0].administrativeInfo.owner.name.charAt(0)}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {campaigns[0].administrativeInfo.owner.name}
                </p>
                <p className="text-xs text-gray-500">
                  {campaigns[0].administrativeInfo.owner.email}
                </p>
              </div>
            </div>
            <div className="text-sm text-gray-600">
              Department: {campaigns[0].administrativeInfo.owner.department}
            </div>
          </div>
        </div>
        
        <div>
          <h4 className="text-md font-semibold text-gray-800 mb-3">Approval Status</h4>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <span className={`
                px-3 py-1 rounded-full text-sm font-medium
                ${
                  campaigns[0].administrativeInfo.approvalStatus === 'approved' 
                    ? 'bg-green-100 text-green-800'
                    : campaigns[0].administrativeInfo.approvalStatus === 'pending'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                }
              `}>
                {campaigns[0].administrativeInfo.approvalStatus.charAt(0).toUpperCase() + 
                 campaigns[0].administrativeInfo.approvalStatus.slice(1)}
              </span>
              {campaigns[0].administrativeInfo.approvedBy && (
                <div className="text-sm text-gray-600 mt-2">
                  Approved by {campaigns[0].administrativeInfo.approvedBy.name} 
                  on {new Date(campaigns[0].administrativeInfo.approvedBy.date).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTechnicalTab = () => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center mb-6">
        <Settings className="h-6 w-6 text-teal-600 mr-3" />
        <h3 className="text-lg font-semibold text-gray-900">Technical Specifications</h3>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-md font-semibold text-gray-800 mb-3">Platforms</h4>
          <ul className="space-y-2">
            {campaigns[0].technicalSpecs.platform.map((platform, index) => (
              <li key={index} className="flex items-center bg-gray-50 p-2 rounded-lg">
                <Settings className="h-5 w-5 text-teal-500 mr-2" />
                <span className="text-sm text-gray-700">{platform}</span>
              </li>
            ))}
          </ul>
        </div>
        
        <div>
          <h4 className="text-md font-semibold text-gray-800 mb-3">Integrations</h4>
          <ul className="space-y-2">
            {campaigns[0].technicalSpecs.integrations.map((integration, index) => (
              <li key={index} className="flex items-center bg-gray-50 p-2 rounded-lg">
                <Zap className="h-5 w-5 text-blue-500 mr-2" />
                <span className="text-sm text-gray-700">{integration}</span>
              </li>
            ))}
          </ul>
        </div>
        
        <div className="md:col-span-2">
          <h4 className="text-md font-semibold text-gray-800 mb-3">Conversion Goals</h4>
          <div className="grid md:grid-cols-2 gap-4">
            {campaigns[0].technicalSpecs.conversionGoals.map((goal, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-800">{goal.type}</span>
                  <span className="text-sm text-gray-600">
                    {goal.current} / {goal.target}
                  </span>
                </div>
                <div className="w-full bg-gray-200 h-2 rounded-full">
                  <div 
                    className="bg-blue-500 h-2 rounded-full" 
                    style={{
                      width: `${(goal.current / goal.target) * 100}%`
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="md:col-span-2">
          <h4 className="text-md font-semibold text-gray-800 mb-3">Tracking Pixels</h4>
          <div className="flex flex-wrap gap-2">
            {campaigns[0].technicalSpecs.trackingPixels.map((pixel, index) => (
              <span 
                key={index} 
                className="px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
              >
                {pixel}
              </span>
            ))}
          </div>
        </div>
        
        <div className="md:col-span-2">
          <div className="bg-gray-50 p-4 rounded-lg flex items-center">
            <Settings className="h-6 w-6 text-green-500 mr-3" />
            <div>
              <h5 className="text-sm font-semibold text-gray-800">AI Optimization</h5>
              <p className="text-xs text-gray-600">
                {campaigns[0].technicalSpecs.aiOptimizationEnabled 
                  ? 'Enabled - Automatic performance improvements' 
                  : 'Disabled'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-8xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Promotion Admin</h1>
              <p className="mt-1 text-sm text-gray-500">
                Comprehensive management of marketing campaigns
              </p>
            </div>
            <div className="flex space-x-3">
              <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
                Export Report
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                Create New Campaign
              </button>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="mb-6">
          <div className="flex space-x-4 border-b border-gray-200">
            {[
              { id: 'overview', label: 'Overview', icon: ChartPie },
              { id: 'marketing', label: 'Marketing', icon: Target },
              { id: 'admin', label: 'Administrative', icon: Settings },
              { id: 'technical', label: 'Technical', icon: Zap }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`
                  flex items-center pb-3 px-4 text-sm font-medium
                  ${activeTab === tab.id 
                    ? 'text-blue-600 border-b-2 border-blue-600' 
                    : 'text-gray-500 hover:text-gray-700'}
                `}
              >
                <tab.icon className={`h-4 w-4 mr-2 ${activeTab === tab.id ? 'text-blue-600' : 'text-gray-400'}`} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'marketing' && renderMarketingTab()}
          {activeTab === 'admin' && renderAdminTab()}
          {activeTab === 'technical' && renderTechnicalTab()}
        </div>
      </div>
    </div>
  );
};

export default PromotionAdminPage;